/**
 * API client for the watchlist and user-preferences endpoints.
 *
 * Endpoints:
 *   POST   /api/v1/watchlist              — add school
 *   DELETE /api/v1/watchlist/:school_id   — remove school
 *   GET    /api/v1/watchlist              — list saved schools with scores
 *   GET    /api/v1/preferences            — get position/year preference
 *   PUT    /api/v1/preferences            — set position/year preference
 *
 * All endpoints require a valid session cookie (HTTP-only, SameSite=Strict).
 * Unauthenticated calls return 401 — callers should handle this by showing
 * the auth prompt rather than propagating the error.
 *
 * Score is nullable per Open Question 2 in work item 06: if no opening_scores
 * row exists for the user's chosen position/year the entry still appears in
 * the list but score/tier/tier_label are null. The UI renders "—" in this case.
 */

export interface WatchlistFallbackScore {
  score: number;
  position: string;
  position_name: string;
}

export interface WatchlistEntry {
  school_id: string;
  name: string;
  slug: string;
  division_name: string;
  region: string;
  city: string;
  state: string;
  /** null when no opening_scores row exists for the chosen position/year. */
  score: number | null;
  /** "high" | "medium" | "low" — null when score is null. */
  tier: 'high' | 'medium' | 'low' | null;
  /** e.g. "HIGH" — null when score is null. */
  tier_label: string | null;
  /** ISO 8601 timestamp when this school was saved. */
  saved_at: string;
  /** ISO 8601 timestamp of the most recent scrape. Null if never scraped. */
  scraped_at: string | null;
  /**
   * WI-26: true when ≥1 player with the selected position in the latest snapshot.
   * Absent when no position is selected.
   */
  has_data?: boolean;
  /**
   * WI-26: coarse parent fallback score when has_data=false and a fine position is queried.
   * Absent otherwise.
   */
  fallback_score?: WatchlistFallbackScore;
  /**
   * WI-44: sport slug saved at bookmark time (e.g. "soccer_mens").
   * Null for entries saved before WI-44 or from contexts without sport.
   */
  sport_slug: string | null;
  /**
   * WI-44: target graduation year saved at bookmark time.
   * Null for entries saved before WI-44 or from contexts without year.
   */
  target_year: number | null;
  /**
   * WI-44: position slugs saved at bookmark time (e.g. ["gk", "def"]).
   * Null for entries saved before WI-44 or with no active position filter.
   */
  position_slugs: string[] | null;
}

export interface UserPreferences {
  position_id: string | null;
  position_slug: string | null;
  target_year: number | null;
}

export interface ApiError {
  error: string;
  code: string;
}

export class WatchlistApiError extends Error {
  public readonly code: string;
  public readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'WatchlistApiError';
    this.code = code;
    this.status = status;
  }
}

async function parseError(response: Response): Promise<WatchlistApiError> {
  try {
    const body: ApiError = await response.json();
    return new WatchlistApiError(body.error, body.code, response.status);
  } catch {
    return new WatchlistApiError(response.statusText, 'unknown', response.status);
  }
}

/**
 * WI-44: Context options carried when saving a school to the watchlist.
 * All fields are optional — callers that don't have search context omit them.
 */
export interface WatchlistSaveOptions {
  sportSlug?: string;
  targetYear?: number;
  /** Multi-select positions from the active search filter. Pass [] to omit. */
  positionSlugs?: string[];
}

/**
 * Add a school to the watchlist, optionally capturing search context.
 * Returns true on first save (201), false on idempotent re-save (200).
 * Throws WatchlistApiError on failure (including 401 Unauthorized).
 *
 * WI-44: options.positionSlugs — an empty array is treated as "no positions"
 * and the field is omitted from the request body (not sent as []).
 */
export async function addToWatchlist(
  schoolId: string,
  options?: WatchlistSaveOptions
): Promise<boolean> {
  const body: Record<string, unknown> = { school_id: schoolId };
  if (options?.sportSlug) body['sport_slug'] = options.sportSlug;
  if (options?.targetYear) body['target_year'] = options.targetYear;
  if (options?.positionSlugs && options.positionSlugs.length > 0) {
    body['position_slugs'] = options.positionSlugs;
  }
  const response = await fetch('/api/v1/watchlist', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await parseError(response);
  return response.status === 201;
}

/**
 * Remove a school from the watchlist.
 * Resolves on success (204). Throws WatchlistApiError on network or auth failure.
 */
export async function removeFromWatchlist(schoolId: string): Promise<void> {
  const response = await fetch(`/api/v1/watchlist/${schoolId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok && response.status !== 204) {
    throw await parseError(response);
  }
}

/**
 * Fetch the current user's watchlist.
 * Optionally filtered by position slug and/or target year.
 * Throws WatchlistApiError on failure.
 */
export async function getWatchlist(
  position?: string,
  targetYear?: number
): Promise<WatchlistEntry[]> {
  const params = new URLSearchParams();
  if (position) params.set('position', position);
  if (targetYear) params.set('target_year', String(targetYear));
  const url = `/api/v1/watchlist${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * Fetch the user's stored position/year preferences.
 * Returns null fields if no preference has been set yet.
 * Throws WatchlistApiError on failure.
 */
export async function getPreferences(): Promise<UserPreferences> {
  const response = await fetch('/api/v1/preferences', { credentials: 'include' });
  if (!response.ok) throw await parseError(response);
  return response.json();
}

/**
 * Save the user's position/year preferences.
 * Throws WatchlistApiError on failure.
 */
export async function setPreferences(
  positionId: string | null,
  targetYear: number | null
): Promise<UserPreferences> {
  const response = await fetch('/api/v1/preferences', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ position_id: positionId, target_year: targetYear }),
  });
  if (!response.ok) throw await parseError(response);
  return response.json();
}
